<template>
  <li
    v-tooltip="{
      content: getTooltipContent(webhook.name),
      placement: 'bottom',
      followCursor: 'vertical',
    }"
    data-cy="webhook-item"
    class="cursor-pointer truncate p-2 whitespace-nowrap text-gray-700 hover:bg-gray-100"
    @mouseenter="checkContentWidth"
    @click="emit('webhook:toggle', webhook.id)"
  >
    <div class="flex justify-between space-x-2">
      <p
        class="overflow-hidden text-ellipsis whitespace-nowrap"
        data-cy="do-templates-dropdown-option-text"
      >
        {{ webhook.name }}
      </p>
      <div class="flex items-center space-x-2">
        <button
          v-if="webhook.parameters?.length"
          v-tooltip="parametersTooltipText"
          @click.stop.prevent="showParameters = !showParameters"
        >
          <icon-base
            :icon="IconCog"
            class="mt-2 hover:text-primary"
            :class="showParameters ? 'text-active-text' : 'text-label'"
          />
        </button>
        <lf-checkbox
          :model-value="checkedItems"
          :name="`webhook-${webhook.id}`"
          :value="webhook.id"
          @update:model-value="emit('webhook:toggle', webhook.id)"
        />
      </div>
    </div>
    <custom-webhook-parameter-list
      v-if="showParameters"
      class="mt-2"
      :parameters="parameters"
      @parameter:change="updateParameters"
    />
  </li>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import CustomWebhookParameterList from "@/components/communication/CustomWebhookParameterList.vue";
import IconCog from "@/components/icons/IconCog.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";

import type { CustomWebhook } from "@/models/customWebhook";
import type { OptionalWorkflowWebhookParameter } from "@/models/workflows";

const emit = defineEmits<{
  "webhook:toggle": [number | string];
  "parameter:change": [OptionalWorkflowWebhookParameter[]];
}>();

const MAX_NAME_LENGTH = 30;

const { webhook, checkedItems, showTooltip } = defineProps<{
  webhook: CustomWebhook;
  checkedItems: number[];
  showTooltip: boolean;
}>();

const { t } = useI18n();

const isTextTruncated = ref(false);
const showParameters = ref(false);

const parameters = ref(
  webhook.parameters?.map((param) => ({ ...param, enabled: true })) || []
);

const parametersTooltipText = computed(() => {
  if (showParameters.value) {
    return `${t("COMMON.HIDE")} ${t("COMMON.PARAMETER", 2)}`;
  }
  return `${t("COMMON.SHOW")} ${t("COMMON.PARAMETER", 2)}`;
});

const getTooltipContent = (name: string) =>
  name.length > MAX_NAME_LENGTH && showTooltip ? name : "";

const checkContentWidth = (event: MouseEvent) => {
  const { offsetWidth, scrollWidth } = event.target as HTMLElement;
  isTextTruncated.value = offsetWidth < scrollWidth;
};

const updateParameters = (
  updatedParameter: OptionalWorkflowWebhookParameter
) => {
  parameters.value = parameters.value.map((parameter) => {
    const { name: uName, type: uType } = updatedParameter;
    const { name, type } = parameter;
    if (name === uName && type === uType) {
      return updatedParameter;
    }
    return parameter;
  });
  emit("parameter:change", parameters.value);
};
</script>
